import type { BaseUser } from "@models";
import {
  COMMENTS_EVENTS,
  hoverOffThread as hoverOffTiptapThread,
  hoverThread as hoverTiptapThread,
  selectThread as selectTiptapThread,
  subscribeToThreads,
} from "@tiptap-pro/extension-comments";
import type { TiptapCollabProvider } from "@tiptap-pro/provider";
import type { Editor } from "@tiptap/react";
import { NodeSelection, type Selection } from "prosemirror-state";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getTiptapCommentAuthorId,
  isTiptapSuggestionCommentThread,
  isTiptapUserCommentThread,
} from "./tiptapCommentUtils";
import type {
  TiptapCommentThread,
  TiptapCommentUser,
  TiptapCommentsController,
  TiptapSelectionState,
} from "./types";

export type TiptapThreadClickEvent = { threadId: string | null; token: number };

type UseTiptapCommentsOptions = {
  clickedThread?: TiptapThreadClickEvent | null;
  currentUser: TiptapCommentUser;
  disabled: boolean;
  editor: Editor | null;
  onCommentCreated?: (
    threadId: string,
    commentContent: string,
    previousCommentContent?: string
  ) => void;
  onThreadCreated?: (threadId: string) => void;
  onThreadSelected?: (threadId: string) => void;
  orgUsers?: BaseUser[];
  provider: TiptapCollabProvider | null;
};

export type StashedTiptapSelection = TiptapSelectionState;

type ContentCommand = (editor: Editor, content: string) => boolean;
type EditorCommand = (editor: Editor) => boolean;
type ThreadCreatedEvent = { thread?: TiptapCommentThread; threadId: string };
type CommentCreatedEvent = { threadId: string };
type CommentUpdatedEvent = { commentId: string; threadId: string };
type PendingComment =
  | { kind: "thread"; commentContent: string }
  | { kind: "reply"; commentContent: string; threadId: string }
  | {
      kind: "edit";
      commentContent: string;
      commentId: string;
      previousCommentContent: string;
      threadId: string;
    };
type TiptapThreadLookup = {
  getThread: (threadId: string) => TiptapCommentThread | null;
};
type ValidSelectionInput = {
  editor: Editor;
  stashedSelection: StashedTiptapSelection;
};
type PreserveSelectionInput = {
  hasSelection: boolean;
  isNewThreadInputOpen: boolean;
  isSelectionInsideEditor: boolean;
};

const SELECTION_CLEAR_KEYS = new Set(["Escape", "Backspace", "Delete"]);
const NON_THREAD_DELETE_OPTIONS = { deleteContent: true, deleteThread: false };
const SELECTED_THREAD_SELECTOR = ".tiptap-thread--selected";

const getSelectionText = (editor: Editor, from: number, to: number) =>
  editor.state.doc.textBetween(from, to, " ").replace(/\s+/g, " ").trim();

const normalizeSelectionText = (text: string) =>
  text.replace(/\s+/g, " ").trim();

const getNodeFingerprint = (node: NodeSelection["node"]) =>
  JSON.stringify({ attrs: node.attrs, type: node.type.name });

const getNodeSelectionText = (editor: Editor, selection: NodeSelection) => {
  const nodeDom = editor.view.nodeDOM(selection.from);
  const renderedText =
    nodeDom instanceof Element
      ? normalizeSelectionText(nodeDom.textContent ?? "")
      : "";
  if (renderedText) {
    return renderedText;
  }

  const attributeText = Object.values(selection.node.attrs).find(
    (value) => typeof value === "string" && value.trim()
  );
  return typeof attributeText === "string"
    ? normalizeSelectionText(attributeText)
    : selection.node.type.name;
};

const getEditorElement = (editor: Editor) => {
  try {
    return editor.view.dom;
  } catch {
    return null;
  }
};

const isDomSelectionInsideEditor = (editor: Editor) => {
  const selection = window.getSelection();
  const editorElement = selection?.rangeCount ? getEditorElement(editor) : null;
  const isInside = (node: Node | null) =>
    Boolean(node && editorElement?.contains(node));
  return (
    isInside(selection?.anchorNode ?? null) ||
    isInside(selection?.focusNode ?? null)
  );
};

const hasVisibleRect = ({ height, width }: DOMRect) => width > 0 || height > 0;

const getSelectionAnchorRect = (editor: Editor, selection: Selection) => {
  if (selection instanceof NodeSelection) {
    const nodeDom = editor.view.nodeDOM(selection.from);
    if (nodeDom instanceof Element) {
      const nodeRect = nodeDom.getBoundingClientRect();
      if (hasVisibleRect(nodeRect)) {
        return nodeRect;
      }
    }
  }

  const domSelection = window.getSelection();
  if (domSelection?.rangeCount && isDomSelectionInsideEditor(editor)) {
    const range = domSelection.getRangeAt(0);
    const rect = Array.from(range.getClientRects()).find(hasVisibleRect);
    if (rect) {
      return rect;
    }
    const boundingRect = range.getBoundingClientRect();
    if (hasVisibleRect(boundingRect)) {
      return boundingRect;
    }
  }
  try {
    const start = editor.view.coordsAtPos(selection.from);
    const end = editor.view.coordsAtPos(selection.to);
    return new DOMRect(
      Math.min(start.left, end.left),
      Math.min(start.top, end.top),
      Math.max(Math.abs(end.right - start.left), 1),
      Math.max(Math.abs(end.bottom - start.top), 1)
    );
  } catch {
    return null;
  }
};

const scrollSelectedThreadToEditorTop = (editor: Editor) => {
  window.requestAnimationFrame(() => {
    const targets = Array.from(
      getEditorElement(editor)?.querySelectorAll<HTMLElement>(
        SELECTED_THREAD_SELECTOR
      ) ?? []
    );

    targets[0]?.scrollIntoView({ behavior: "smooth", block: "start" });
    targets.forEach((target) =>
      target.animate([{ opacity: 0.15 }, { opacity: 1 }], {
        delay: 300,
        duration: 2000,
        easing: "ease-out",
      })
    );
  });
};

export const getStashedTiptapSelection = (
  editor: Editor,
  selection: Selection
): StashedTiptapSelection | null => {
  if (selection instanceof NodeSelection) {
    const text = getNodeSelectionText(editor, selection);
    return {
      anchorRect: getSelectionAnchorRect(editor, selection),
      from: selection.from,
      kind: "node",
      nodeFingerprint: getNodeFingerprint(selection.node),
      nodeInline: selection.node.isInline,
      nodeType: selection.node.type.name,
      text,
      to: selection.to,
    };
  }

  const { empty, from, to } = selection;
  const text = empty || from === to ? "" : getSelectionText(editor, from, to);
  return text
    ? {
        anchorRect: getSelectionAnchorRect(editor, selection),
        from,
        kind: "text",
        text,
        to,
      }
    : null;
};

export const getValidStashedTiptapSelectionText = ({
  editor,
  stashedSelection,
}: ValidSelectionInput) => {
  if (stashedSelection.kind === "node") {
    const selectedNode = editor.state.doc.nodeAt(stashedSelection.from);
    return selectedNode &&
      getNodeFingerprint(selectedNode) === stashedSelection.nodeFingerprint
      ? stashedSelection.text
      : null;
  }

  const selectedText = getSelectionText(
    editor,
    stashedSelection.from,
    stashedSelection.to
  );
  return selectedText === stashedSelection.text ? selectedText : null;
};

export const getUserCommentThreadFromEvent = ({
  provider,
  thread,
  threadId,
}: {
  provider?: TiptapThreadLookup | null;
  thread?: TiptapCommentThread | null;
  threadId: string;
}) => {
  const candidateThread = thread ?? provider?.getThread(threadId) ?? null;
  return candidateThread && isTiptapUserCommentThread(candidateThread)
    ? candidateThread
    : null;
};

export const shouldPreserveStashedTiptapSelection = ({
  hasSelection,
  isNewThreadInputOpen,
  isSelectionInsideEditor,
}: PreserveSelectionInput) =>
  isNewThreadInputOpen && hasSelection && !isSelectionInsideEditor;

export const createTiptapThreadClickEvent = (
  threadId: string | null,
  previousEvent: TiptapThreadClickEvent | null
): TiptapThreadClickEvent => ({
  threadId,
  token: (previousEvent?.token ?? 0) + 1,
});

export const useTiptapComments = ({
  clickedThread,
  currentUser,
  disabled,
  editor,
  onCommentCreated,
  onThreadCreated,
  onThreadSelected,
  orgUsers = [],
  provider,
}: UseTiptapCommentsOptions): TiptapCommentsController => {
  const [allThreads, setAllThreads] = useState<TiptapCommentThread[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [selection, setSelection] = useState<TiptapSelectionState | null>(null);
  const [isNewThreadInputOpen, setNewThreadInputOpen] = useState(false);
  const selectionRef = useRef<StashedTiptapSelection | null>(null);
  const pendingCommentRef = useRef<PendingComment | null>(null);
  const threads = useMemo(
    () => allThreads.filter(isTiptapUserCommentThread),
    [allThreads]
  );
  const suggestionThreads = useMemo(
    () => allThreads.filter(isTiptapSuggestionCommentThread),
    [allThreads]
  );
  const clearSelection = useCallback(() => {
    selectionRef.current = null;
    setSelection(null);
    setNewThreadInputOpen(false);
  }, []);
  const updateSelection = useCallback(
    (nextSelection: StashedTiptapSelection) => {
      selectionRef.current = nextSelection;
      setSelection({ ...nextSelection });
    },
    []
  );
  const refreshSelection = useCallback(() => {
    if (!editor || disabled) {
      clearSelection();
      return;
    }
    const isNodeSelection = editor.state.selection instanceof NodeSelection;
    const isSelectionInsideEditor =
      isNodeSelection || isDomSelectionInsideEditor(editor);
    const nextSelection = isSelectionInsideEditor
      ? getStashedTiptapSelection(editor, editor.state.selection)
      : null;
    if (nextSelection) {
      updateSelection(nextSelection);
      return;
    }
    if (
      !shouldPreserveStashedTiptapSelection({
        hasSelection: Boolean(selectionRef.current),
        isNewThreadInputOpen,
        isSelectionInsideEditor,
      })
    ) {
      clearSelection();
    }
  }, [clearSelection, disabled, editor, isNewThreadInputOpen, updateSelection]);
  useEffect(() => {
    if (!provider) {
      setAllThreads([]);
      setSelectedThreadId(null);
      return;
    }
    return subscribeToThreads({
      provider,
      callback: (nextThreads) =>
        setAllThreads(nextThreads as TiptapCommentThread[]),
      getThreadsOptions: { types: ["unarchived"] },
    });
  }, [provider]);
  useEffect(() => {
    if (!editor) {
      clearSelection();
      return;
    }
    editor.on("selectionUpdate", refreshSelection);
    document.addEventListener("selectionchange", refreshSelection);
    return () => {
      editor.off("selectionUpdate", refreshSelection);
      document.removeEventListener("selectionchange", refreshSelection);
    };
  }, [clearSelection, editor, refreshSelection]);
  useEffect(() => {
    const editorElement = editor ? getEditorElement(editor) : null;
    if (!editorElement) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (SELECTION_CLEAR_KEYS.has(event.key)) {
        clearSelection();
      }
    };
    editorElement.addEventListener("keydown", handleKeyDown);
    return () => {
      editorElement.removeEventListener("keydown", handleKeyDown);
    };
  }, [clearSelection, editor]);
  useEffect(() => {
    if (disabled) {
      clearSelection();
    }
  }, [clearSelection, disabled]);
  useEffect(() => {
    if (!clickedThread?.threadId) {
      return;
    }
    setSelectedThreadId(clickedThread.threadId);
    onThreadSelected?.(clickedThread.threadId);
  }, [clickedThread, onThreadSelected]);
  useEffect(() => {
    if (!editor) {
      return;
    }
    const handleThreadCreated = ({ thread, threadId }: ThreadCreatedEvent) => {
      const persistedThread = getUserCommentThreadFromEvent({
        provider,
        thread,
        threadId,
      });
      if (persistedThread) {
        setSelectedThreadId(threadId);
        onThreadCreated?.(threadId);

        const pendingComment = pendingCommentRef.current;
        if (pendingComment?.kind === "thread") {
          pendingCommentRef.current = null;

          const commentWasCreated = persistedThread.comments.some(
            (comment) =>
              comment.content === pendingComment.commentContent &&
              getTiptapCommentAuthorId(comment, persistedThread) ===
                currentUser.id
          );
          if (commentWasCreated) {
            onCommentCreated?.(threadId, pendingComment.commentContent);
          }
        }
      }
    };
    const handleCommentCreated = ({ threadId }: CommentCreatedEvent) => {
      const pendingComment = pendingCommentRef.current;
      if (
        pendingComment?.kind === "reply" &&
        pendingComment.threadId === threadId
      ) {
        pendingCommentRef.current = null;
        onCommentCreated?.(threadId, pendingComment.commentContent);
      }
    };
    const handleCommentUpdated = ({
      commentId,
      threadId,
    }: CommentUpdatedEvent) => {
      const pendingComment = pendingCommentRef.current;
      if (
        pendingComment?.kind === "edit" &&
        pendingComment.commentId === commentId &&
        pendingComment.threadId === threadId
      ) {
        pendingCommentRef.current = null;
        onCommentCreated?.(
          threadId,
          pendingComment.commentContent,
          pendingComment.previousCommentContent
        );
      }
    };
    editor.on(COMMENTS_EVENTS.THREAD_CREATED, handleThreadCreated);
    editor.on(COMMENTS_EVENTS.COMMENT_CREATED, handleCommentCreated);
    editor.on(COMMENTS_EVENTS.COMMENT_UPDATED, handleCommentUpdated);
    return () => {
      editor.off(COMMENTS_EVENTS.THREAD_CREATED, handleThreadCreated);
      editor.off(COMMENTS_EVENTS.COMMENT_CREATED, handleCommentCreated);
      editor.off(COMMENTS_EVENTS.COMMENT_UPDATED, handleCommentUpdated);
    };
  }, [currentUser.id, editor, onCommentCreated, onThreadCreated, provider]);
  const canCreateThread = Boolean(
    editor && provider && !disabled && selection?.text.trim()
  );
  const runEditorCommand = useCallback(
    (command: EditorCommand) => (!editor || disabled ? false : command(editor)),
    [disabled, editor]
  );
  const runContentCommand = useCallback(
    (content: string, command: ContentCommand) => {
      const trimmedContent = content.trim();
      return trimmedContent
        ? runEditorCommand((editor) => command(editor, trimmedContent))
        : false;
    },
    [runEditorCommand]
  );
  const openNewThreadInput = useCallback(() => {
    if (canCreateThread) {
      setNewThreadInputOpen(true);
    }
  }, [canCreateThread]);
  const createThread = useCallback(
    (content: string) => {
      const stashedSelection = selectionRef.current;
      if (!provider || !stashedSelection) {
        return false;
      }
      return runContentCommand(content, (editor, trimmedContent) => {
        const quote = getValidStashedTiptapSelectionText({
          editor,
          stashedSelection,
        });
        if (!quote) {
          clearSelection();
          return false;
        }
        const chain = editor.chain();
        if (stashedSelection.kind === "node" && !stashedSelection.nodeInline) {
          chain.setNodeSelection(stashedSelection.from);
        } else {
          chain.setTextSelection({
            from: stashedSelection.from,
            to: stashedSelection.to,
          });
        }
        const pendingComment: PendingComment = {
          kind: "thread",
          commentContent: trimmedContent,
        };
        pendingCommentRef.current = pendingComment;
        const created = chain
          .setThread({
            content: trimmedContent,
            data: { quote, user: currentUser },
            commentData: { user: currentUser },
          })
          .run();
        if (pendingCommentRef.current === pendingComment) {
          pendingCommentRef.current = null;
        }
        if (created) {
          editor.commands.focus(stashedSelection.to);
          clearSelection();
        }
        return created;
      });
    },
    [clearSelection, currentUser, provider, runContentCommand]
  );
  const createReply = useCallback(
    (threadId: string, content: string) =>
      runContentCommand(content, (editor, trimmedContent) => {
        const pendingComment: PendingComment = {
          kind: "reply",
          commentContent: trimmedContent,
          threadId,
        };
        pendingCommentRef.current = pendingComment;
        const handled = editor.commands.createComment({
          threadId,
          content: trimmedContent,
          data: { user: currentUser },
        });
        if (pendingCommentRef.current === pendingComment) {
          pendingCommentRef.current = null;
        }
        return handled;
      }),
    [currentUser, runContentCommand]
  );
  const editComment = useCallback(
    (threadId: string, commentId: string, content: string) =>
      runContentCommand(content, (editor, trimmedContent) => {
        const previousCommentContent = provider
          ?.getThread(threadId)
          ?.comments.find(({ id }) => id === commentId)?.content;
        if (previousCommentContent === undefined) {
          return false;
        }
        const pendingComment: PendingComment = {
          kind: "edit",
          commentContent: trimmedContent,
          commentId,
          previousCommentContent,
          threadId,
        };
        pendingCommentRef.current = pendingComment;
        const handled = editor.commands.updateComment({
          threadId,
          id: commentId,
          content: trimmedContent,
          data: { user: currentUser },
        });
        if (pendingCommentRef.current === pendingComment) {
          pendingCommentRef.current = null;
        }
        return handled;
      }),
    [currentUser, provider, runContentCommand]
  );
  const deleteThread = useCallback(
    (threadId: string) =>
      runEditorCommand((editor) => {
        const removed = editor.commands.removeThread({
          id: threadId,
          deleteThread: true,
        });
        if (removed && selectedThreadId === threadId) {
          setSelectedThreadId(null);
        }
        return removed;
      }),
    [runEditorCommand, selectedThreadId]
  );
  const deleteComment = useCallback(
    (threadId: string, commentId: string) => {
      const thread = threads.find((candidate) => candidate.id === threadId);
      const firstComment = thread?.comments.find(
        (comment) => !comment.deletedAt
      );
      return firstComment?.id === commentId
        ? deleteThread(threadId)
        : runEditorCommand((editor) =>
            editor.commands.removeComment({
              threadId,
              id: commentId,
              ...NON_THREAD_DELETE_OPTIONS,
            })
          );
    },
    [deleteThread, runEditorCommand, threads]
  );
  const resolveThread = useCallback(
    (threadId: string) =>
      runEditorCommand((editor) =>
        editor.commands.resolveThread({ id: threadId })
      ),
    [runEditorCommand]
  );
  const reopenThread = useCallback(
    (threadId: string) =>
      runEditorCommand((editor) =>
        editor.commands.unresolveThread({ id: threadId })
      ),
    [runEditorCommand]
  );
  const selectThread = useCallback(
    (threadId: string) => {
      if (!editor) {
        return false;
      }
      setSelectedThreadId(threadId);
      const selectedInDocument = editor.commands.selectThread({
        id: threadId,
        focus: true,
        scrollIntoView: false,
      });

      if (!selectedInDocument) {
        selectTiptapThread(editor, threadId);
      }

      scrollSelectedThreadToEditorTop(editor);
      onThreadSelected?.(threadId);
      return selectedInDocument;
    },
    [editor, onThreadSelected]
  );
  const hoverThread = useCallback(
    (threadId: string) => {
      if (editor) hoverTiptapThread(editor, [threadId]);
    },
    [editor]
  );
  const unhoverThread = useCallback(
    (threadId: string) => {
      if (editor) hoverOffTiptapThread(editor, [threadId]);
    },
    [editor]
  );
  return useMemo(
    () => ({
      canCreateThread,
      clearSelection,
      createReply,
      createThread,
      currentUser,
      deleteComment,
      deleteThread,
      disabled,
      editComment,
      editor,
      hoverThread,
      isNewThreadInputOpen,
      openNewThreadInput,
      orgUsers,
      reopenThread,
      resolveThread,
      selectThread,
      selectedThreadId,
      selection,
      setNewThreadInputOpen,
      suggestionThreads,
      threads,
      unhoverThread,
    }),
    [
      canCreateThread,
      clearSelection,
      createReply,
      createThread,
      currentUser,
      deleteComment,
      deleteThread,
      disabled,
      editComment,
      editor,
      hoverThread,
      isNewThreadInputOpen,
      openNewThreadInput,
      orgUsers,
      reopenThread,
      resolveThread,
      selectThread,
      selectedThreadId,
      selection,
      suggestionThreads,
      threads,
      unhoverThread,
    ]
  );
};
